import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import LandingPage from "@/pages/LandingPage";
import SearchPage from "@/pages/SearchPage";
import FacilityPage from "@/pages/FacilityPage";
import FavoritesPage from "@/pages/FavoritesPage";
import DirectorDashboard from "@/pages/DirectorDashboard";
import ForFacilitiesPage from "@/pages/ForFacilitiesPage";
import ClaimPage from "@/pages/ClaimPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AdminBlogPage from "@/pages/AdminBlogPage";
import AdminConsole from "@/pages/AdminConsole";
import { SmsTermsPage, PrivacyPage } from "@/pages/LegalPages";
import AboutPage from "@/pages/AboutPage";
import { Footer } from "@/components/Footer";
import { Loader2 } from "lucide-react";

function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
        await refresh();
      } catch (e) { console.error("Auth session exchange failed", e); }
      window.history.replaceState({}, "", "/search");
      navigate("/search", { replace: true });
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
      <Loader2 className="h-8 w-8 animate-spin text-[#1A365D]" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  // Footer hidden on app-like/full-height views (map, dashboards).
  const noFooter = ["/search", "/favorites", "/director", "/admin", "/admin/blog", "/claim"];
  const showFooter = !noFooter.includes(location.pathname);
  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-white focus:text-[#1A365D] focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:font-medium" data-testid="skip-to-main">
        Skip to main content
      </a>
      <main id="main-content">
        <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/facility/:id" element={<FacilityPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/director" element={<DirectorDashboard />} />
        <Route path="/for-facilities" element={<ForFacilitiesPage />} />
        <Route path="/claim" element={<ClaimPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/admin" element={<AdminConsole />} />
        <Route path="/admin/blog" element={<AdminBlogPage />} />
        <Route path="/sms-terms" element={<SmsTermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
      </main>
      {showFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
